import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getActiveRole, setActiveRoleStorage } from "@/lib/api";
import { isAdminLike, isStaff } from "@/lib/roles";

const AuthContext = createContext(null);

const isOnboardingComplete = (user, profile) => {
  if (!user) return false;
  // Staff (admins + coaches) and pending users don't need player onboarding
  if (user.role === "pending" || isStaff(user.role)) return true;
  if (!profile) return false;
  return Boolean((profile.rut || "").trim()) && Boolean((profile.emergency_phone || "").trim());
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/players/me");
      setProfile(data);
      return data;
    } catch {
      setProfile(null);
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // Validate stored active role: must be in the user's authorized list.
      // If not (or missing), drop the override → backend will use primary role.
      const stored = getActiveRole();
      const allowed = data.authorized_roles || [];
      if (stored && !allowed.includes(stored)) {
        setActiveRoleStorage(null);
      }
      setUser(data);
      if (data.role !== "pending" && !isAdminLike(data.role)) {
        await fetchProfile();
      } else {
        setProfile(null);
      }
    } catch {
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [fetchProfile]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setActiveRoleStorage(null);
    setUser(null);
    setProfile(null);
  };

  // Switch the active role. Persists to sessionStorage and refetches /auth/me
  // (which returns role=active_role after backend override).
  const switchRole = useCallback(async (role) => {
    if (!user) return;
    const allowed = user.authorized_roles || [];
    if (!allowed.includes(role)) return;
    setActiveRoleStorage(role === user.primary_role ? null : role);
    await checkAuth();
  }, [user, checkAuth]);

  const onboardingCompleted = isOnboardingComplete(user, profile);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      profile,
      setProfile,
      loading,
      refresh: checkAuth,
      refreshProfile: fetchProfile,
      onboardingCompleted,
      logout,
      switchRole,
      activeRole: user?.active_role,
      primaryRole: user?.primary_role,
      authorizedRoles: user?.authorized_roles || [],
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
